import { stripeService, CREDIT_COSTS } from '../services/stripeService';

// Default Tier Limits (for initial state)
const TIER_LIMITS = {
    basic: { resume_builds: 1, score_checks: 5, job_matches: 3, cover_letters: 3, custom_templates: 0 },
    pro: { resume_builds: 999999, score_checks: 30, job_matches: 999999, cover_letters: 20, custom_templates: 5 },
    max: { resume_builds: 999999, score_checks: 999999, job_matches: 999999, cover_letters: 999999, custom_templates: 15 }
};

class SubscriptionManager {
    constructor() {
        this.subscription = null;
        this.listeners = [];
    }

    subscribe(listener) {
        this.listeners.push(listener);
        if (this.subscription) listener(this.subscription);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    notifyListeners() {
        this.listeners.forEach(listener => listener(this.subscription));
    }

    async loadSubscription(email) {
        if (!email) return;
        try {
            this.subscription = await stripeService.getSubscription(email);
        } catch (err) {
            console.error('[SubscriptionManager] Load error:', err);
            this.subscription = {
                tier: 'basic',
                usage: { resume_builds: 0, score_checks: 0, job_matches: 0, cover_letters: 0, custom_templates: 0 },
                credit_balance: 0,
                limits: TIER_LIMITS.basic
            };
        }
        this.notifyListeners();
    }

    async recordUsage(email, feature) {
        if (!email) return { success: false, error: 'No email provided' };
        try {
            const result = await stripeService.recordUsage(email, feature);
            // Update local subscription state with the response
            this.subscription = {
                ...this.subscription,
                usage: result.usage || this.subscription?.usage,
                credit_balance: result.credit_balance ?? this.subscription?.credit_balance ?? 0,
                limits: result.limits || this.subscription?.limits
            };
            this.notifyListeners();
            return {
                success: true,
                credits_used: result.credits_used || false,
                credits_deducted: result.credits_deducted || 0,
                credit_balance: result.credit_balance ?? 0
            };
        } catch (err) {
            console.error('[SubscriptionManager] Record usage error:', err);
            return {
                success: false,
                error: err.message,
                limitReached: err.limitReached || false,
                can_buy_credits: err.can_buy_credits || false,
                credit_cost: err.credit_cost || 0,
                credit_balance: err.credit_balance || 0
            };
        }
    }

    canUse(feature) {
        if (!this.subscription) return true; // Default to basic limits handling by server
        const limit = this.subscription.limits?.[feature];

        // Boolean features (e.g. ai_tailored_resume, pdf_export): true = allowed, false = blocked
        if (typeof limit === 'boolean') return limit;

        // Unlimited numeric features
        if (limit >= 999999 || limit === Infinity || limit === -1) return true;

        const used = this.subscription.usage?.[feature] || 0;
        if (used < (limit || 0)) return true;

        // Over tier limit — check if credits can cover
        const creditCost = CREDIT_COSTS[feature];
        if (creditCost && (this.subscription.credit_balance || 0) >= creditCost) return true;

        return false;
    }

    // Check specifically if the feature will use credits (over tier limit but has credits)
    willUseCredits(feature) {
        if (!this.subscription) return false;
        const limit = this.subscription.limits?.[feature];

        if (typeof limit === 'boolean') return false;
        if (limit >= 999999 || limit === Infinity || limit === -1) return false;

        const used = this.subscription.usage?.[feature] || 0;
        if (used < (limit || 0)) return false; // Still within tier limits

        // Over limit — check credits
        const creditCost = CREDIT_COSTS[feature];
        return creditCost && (this.subscription.credit_balance || 0) >= creditCost;
    }

    getCreditBalance() {
        return this.subscription?.credit_balance || 0;
    }

    clear() {
        this.subscription = null;
        this.notifyListeners();
    }
}

export const subscriptionManager = new SubscriptionManager();
